/**
 * Gestión → Administración → Cotización (listado)
 * (`/gestion/cotizaciones`).
 *
 * Puerto nativo de `Cotizacion::index` + `views/menu/cotizacion/index.php`:
 * lista las cotizaciones vigentes y abre un modal con los productos y métodos
 * de pago. El alta vive en `cotizacion-form-page`.
 */

import { Eye, FileText, Plus } from 'lucide-react';
import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { AdministracionNav } from '../components/administracion-nav';
import { fechaCorta, fechaHora, monto } from '../gestion-formato';
import { useCotizacionDetalle, useCotizaciones } from '../cotizacion.api';

export function CotizacionListadoPage() {
  useDocumentTitle('Gestión · Cotización');

  const navegar = useNavigate();
  const [detalleId, setDetalleId] = useState<number | null>(null);

  const listado = useCotizaciones();
  const detalle = useCotizacionDetalle(detalleId);

  const cerrar = () => setDetalleId(null);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Cotización</h1>
          <p className="mt-1 text-sm text-ink-500">
            Cotizaciones vigentes con sus productos y métodos de pago.
          </p>
        </div>

        <Button
          size="sm"
          leftIcon={<Plus className="size-4" aria-hidden="true" />}
          onClick={() => navegar('/gestion/cotizaciones/nueva')}
        >
          Nueva cotización
        </Button>
      </header>

      <AdministracionNav active="cotizacion" />

      {listado.isError ? (
        <Alert variant="error" title="No se pudieron leer las cotizaciones">
          {listado.error instanceof ApiError ? listado.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {listado.isLoading ? <Skeleton className="h-48 w-full" /> : null}

      {listado.data ? (
        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                  <tr>
                    <th className="py-2 pr-3 font-semibold">Serie-N°</th>
                    <th className="py-2 pr-3 font-semibold">Fecha</th>
                    <th className="py-2 pr-3 font-semibold">Cliente</th>
                    <th className="py-2 pr-3 font-semibold">Término de pago</th>
                    <th className="py-2 pr-3 font-semibold text-right">Total</th>
                    <th className="py-2 pr-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-50">
                  {listado.data.cotizaciones.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-sm text-ink-400">
                        Todavía no hay cotizaciones. Registra la primera con «Nueva cotización».
                      </td>
                    </tr>
                  ) : (
                    listado.data.cotizaciones.map((cotizacion) => (
                      <tr key={cotizacion.id} className="hover:bg-ink-50/50">
                        <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-ink-700">
                          {cotizacion.serie}-{cotizacion.id}
                        </td>
                        <td className="py-2.5 pr-3 text-ink-600">
                          {fechaCorta(cotizacion.fecha_emision)}
                        </td>
                        <td className="py-2.5 pr-3">
                          <p className="font-medium text-ink-800">{cotizacion.cliente_nombre}</p>
                          <p className="text-xs text-ink-400">{cotizacion.cliente_numero_doc}</p>
                        </td>
                        <td className="py-2.5 pr-3 text-ink-600">
                          {cotizacion.termino_pago_descripcion}
                        </td>
                        <td className="py-2.5 pr-3 text-right font-semibold tabular-nums">
                          S/ {monto(cotizacion.total)}
                        </td>
                        <td className="py-2.5 pr-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Ver detalle"
                            onClick={() => setDetalleId(cotizacion.id)}
                          >
                            <Eye className="size-4" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={detalleId !== null}
        onClose={cerrar}
        title={detalle.data ? `Cotización ${detalle.data.cotizacion.serie}-${detalle.data.cotizacion.id}` : 'Detalle de cotización'}
        description="Productos y métodos de pago registrados."
        size="lg"
        footer={
          <Button variant="outline" size="sm" onClick={cerrar}>
            Cerrar
          </Button>
        }
      >
        {detalle.isLoading ? <Skeleton className="h-48 w-full" /> : null}

        {detalle.isError ? (
          <Alert variant="error" title="No se pudo leer el detalle">
            {detalle.error instanceof ApiError ? detalle.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {detalle.data ? (
          <div className="space-y-5">
            <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <CampoDetalle etiqueta="Cliente" valor={detalle.data.cotizacion.cliente_nombre} />
              <CampoDetalle etiqueta="Documento" valor={detalle.data.cotizacion.cliente_numero_doc} />
              <CampoDetalle etiqueta="Término de pago" valor={detalle.data.cotizacion.termino_pago_descripcion} />
              <CampoDetalle etiqueta="Tiempo de validez" valor={detalle.data.cotizacion.tiempo_validez} />
              <CampoDetalle etiqueta="Tiempo de entrega" valor={detalle.data.cotizacion.tiempo_entrega} />
              <CampoDetalle etiqueta="Número de cuenta" valor={detalle.data.cotizacion.numero_cuenta} />
              <CampoDetalle etiqueta="Contacto" valor={detalle.data.cotizacion.contacto} />
              <CampoDetalle etiqueta="Teléfono" valor={detalle.data.cotizacion.telefono} />
              <CampoDetalle etiqueta="Observación" valor={detalle.data.cotizacion.observacion} />
              <CampoDetalle
                etiqueta="Información referencial"
                valor={detalle.data.cotizacion.informacion_referencial}
              />
              <CampoDetalle etiqueta="Registrada por" valor={detalle.data.cotizacion.nombre_usuario} />
              <CampoDetalle etiqueta="Registrada el" valor={fechaHora(detalle.data.cotizacion.created_at)} />
            </div>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
                Productos
              </h3>
              <div className="overflow-x-auto rounded-control border border-ink-100">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Cantidad</th>
                      <th className="px-3 py-2 font-semibold">Descripción</th>
                      <th className="px-3 py-2 font-semibold text-right">Precio</th>
                      <th className="px-3 py-2 font-semibold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-50">
                    {detalle.data.productos.map((producto) => (
                      <tr key={producto.id}>
                        <td className="px-3 py-2 tabular-nums">{monto(producto.cantidad)}</td>
                        <td className="px-3 py-2">
                          {producto.unidad ? `${producto.unidad} - ` : ''}
                          {producto.producto_nombre}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          S/ {monto(producto.precio)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">
                          S/ {monto(producto.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
                Métodos de pago
              </h3>
              <div className="overflow-x-auto rounded-control border border-ink-100">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Método</th>
                      <th className="px-3 py-2 font-semibold">Destino</th>
                      <th className="px-3 py-2 font-semibold">Referencia</th>
                      <th className="px-3 py-2 font-semibold text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-50">
                    {detalle.data.metodos_pago.map((metodo) => (
                      <tr key={metodo.id}>
                        <td className="px-3 py-2">{metodo.metodo_termino_pago_descripcion}</td>
                        <td className="px-3 py-2">{metodo.destino_banco_descripcion}</td>
                        <td className="px-3 py-2">{metodo.referencia ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">
                          S/ {monto(metodo.monto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="flex items-center justify-between rounded-control bg-ink-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm text-ink-500">
                <FileText className="size-4" aria-hidden="true" />
                Total de la cotización
              </span>
              <span className="text-base font-bold text-ink-900">
                S/ {monto(detalle.data.cotizacion.total)}
              </span>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function CampoDetalle({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-ink-50 py-1">
      <dt className="shrink-0 text-xs text-ink-400">{etiqueta}</dt>
      <dd className="text-right text-sm text-ink-700">{valor ?? '—'}</dd>
    </div>
  );
}
